import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../context/AuthContext';
import supabase from '../../../supabase/supabase';
import SafeIcon from '../../../common/SafeIcon';

const ParticipantMentoring = () => {
  const { user } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);

  const roadmap = [
    { week: 1, title: "Strategic Vision & Faith-Led Leadership", focus: "Aligning personal values with corporate mission." },
    { week: 2, title: "Operational Clarity", focus: "Auditing current systems and identifying bottlenecks." },
    { week: 3, title: "Financial Resilience", focus: "Understanding margins, ROI, and sustainable growth." },
    { week: 4, title: "CQC Excellence Framework", focus: "Mapping the Combined Operating System to KLOEs." },
    { week: 5, title: "High-Performance Culture", focus: "Recruitment, retention, and pastoral support." },
    { week: 6, title: "Clinical Governance", focus: "Robust QA systems and risk management." },
    { week: 7, title: "Strategic Tendering", focus: "Securing high-value local authority contracts." },
    { week: 8, title: "Middle Management Empowerment", focus: "Delegation and succession planning." },
    { week: 9, title: "Crisis Management", focus: "Navigating inspections and staffing crises." },
    { week: 10, title: "The Road Ahead", focus: "Sustainability and world-class standards." }
  ];

  useEffect(() => {
    const fetchSessions = async () => {
      const { data } = await supabase
        .from('mentoring_sessions_1741860000000')
        .select('*')
        .eq('user_id', user.id)
        .order('week_number', { ascending: true });

      setSessions(data || []);
      setLoading(false);
    };
    fetchSessions();
  }, [user]);

  const completedCount = sessions.filter(s => s.status === 'Completed').length;
  const progressPercent = (completedCount / 10) * 100;

  return (
    <div className="max-w-4xl mx-auto">
      <header className="mb-8">
        <h1 className="text-3xl font-serif font-bold text-navy-900">Leadership Mentoring Tracker</h1>
        <p className="text-slate-500">Your 10-week strategic roadmap to excellence.</p>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
        <div className="bg-navy-900 text-white p-8 rounded-2xl shadow-xl md:col-span-2">
          <div className="flex justify-between items-end mb-6">
            <div>
              <p className="text-gold-500 text-sm font-bold uppercase tracking-wider mb-1">Programme Progress</p>
              <h2 className="text-3xl font-bold">Week {completedCount + 1} of 10</h2>
            </div>
            <div className="text-right">
              <span className="text-3xl font-bold text-gold-500">{progressPercent}%</span>
            </div>
          </div>
          <div className="w-full bg-white/10 rounded-full h-3">
            <div className="bg-gold-500 h-3 rounded-full transition-all duration-1000" style={{ width: `${progressPercent}%` }}></div>
          </div>
        </div>
        
        <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm flex flex-col justify-center text-center">
          <SafeIcon name="Target" className="w-10 h-10 text-gold-500 mx-auto mb-3" />
          <p className="text-slate-500 text-xs font-bold uppercase tracking-wider">Next Milestone</p>
          <p className="text-navy-900 font-bold mt-1 text-sm">{roadmap[completedCount]?.title || "Programme Complete"}</p>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="p-6 border-b border-slate-200 bg-slate-50">
          <h3 className="font-bold text-navy-900">Your 10-Week Roadmap</h3>
        </div>
        <div className="divide-y divide-slate-100">
          {roadmap.map((item) => {
            const isCompleted = completedCount >= item.week;
            const isCurrent = completedCount + 1 === item.week;
            return (
              <div key={item.week} className="p-6 flex items-center justify-between hover:bg-slate-50 transition-colors">
                <div className="flex items-center gap-4">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold border-2 ${
                    isCompleted ? 'bg-green-50 border-green-200 text-green-600' : 
                    isCurrent ? 'bg-navy-50 border-navy-200 text-navy-900' : 'bg-slate-50 border-slate-200 text-slate-400'
                  }`}>
                    {item.week}
                  </div>
                  <div>
                    <h4 className={`font-bold ${isCurrent ? 'text-navy-900' : 'text-slate-700'}`}>{item.title}</h4>
                    <p className="text-xs text-slate-500">{item.focus}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  {isCompleted ? (
                    <span className="text-xs font-bold px-3 py-1 bg-green-100 text-green-700 rounded-full">Completed</span>
                  ) : isCurrent ? (
                    <span className="text-xs font-bold px-3 py-1 bg-blue-100 text-blue-700 rounded-full">Active</span>
                  ) : (
                    <span className="text-xs font-bold px-3 py-1 bg-slate-100 text-slate-400 rounded-full">Scheduled</span>
                  )}
                  <button className="text-slate-300 hover:text-navy-900">
                    <SafeIcon name="ChevronRight" className="w-5 h-5" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ParticipantMentoring;